import { useCallback, useEffect, useState } from 'react'
import axios from 'axios'
import { StoreGeoSearch } from 'types/storeGeoSearch'
import { fetchStoreGeoSearch } from 'repos/storeGeoSearch'
import { auth, db, FirebaseTimestamp, GeoPoint } from 'firebase/index'
import { GENERATE_STORES_GEO_SEARCH } from 'constants/urls'
import { StoreType } from 'types/store'
import { useDispatch } from 'react-redux'
import { hideLoading, showLoading } from 'reducks/loading/operations'

type PickedStoreType = Pick<StoreType, 'address' | 'is_sacri_station' | 'name' | 'image_urls' | 'status'>

type LatLng = {
  lat: number
  lng: number
}

type UseStoreGeoSearch = {
  center: LatLng
  generateStoreGeoSearch: (store: PickedStoreType) => Promise<void>
  handlePosition: (lat: number, lng: number) => void
  position: LatLng
  storeGeoSearch: StoreGeoSearch | undefined
}

const useStoreGeoSearch = (storeId: string): UseStoreGeoSearch => {
  const dispatch = useDispatch()

  const [storeGeoSearch, setStoreGeoSearch] = useState<StoreGeoSearch | undefined>(undefined)
  const [center, setCenter] = useState<LatLng>({ lat: 0, lng: 0 })
  const [position, setPosition] = useState<LatLng>({ lat: 0, lng: 0 })

  const handlePosition = useCallback(
    (lat: number, lng: number) => {
      setPosition({ lat, lng })
      setStoreGeoSearch((prevState) => {
        if (!prevState) return
        const nextState = prevState
        nextState.l = new GeoPoint(lat, lng)
        nextState.d.coordinates = new GeoPoint(lat, lng)
        return { ...nextState }
      })
    },
    [setPosition]
  )

  const generateStoreGeoSearch = useCallback(
    async (store: PickedStoreType) => {
      // トークンを再取得する
      dispatch(showLoading())
      const token = await auth.currentUser?.getIdToken()
      if (typeof !token === 'undefined') {
        console.error('認証トークンの取得に失敗しました。')
        dispatch(hideLoading())
        return
      }

      try {
        const { state, city, street, building } = store.address
        const address = [state, city, street, building].join('')

        const res = await axios.post(
          GENERATE_STORES_GEO_SEARCH,
          { address },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
              'Access-Control-Allow-Origin': '*',
            },
          }
        )

        if (res.data.status !== 'succeeded') {
          throw Error(res.data.message)
        }

        const { latlng, g } = res.data
        const { lat, lng } = latlng

        const storeRef = db.collection('stores').doc(storeId)
        const geoSnapshot = await db.collection('stores_geo_search').doc(storeId).get()
        const beforeStoreGeoSearch = geoSnapshot.exists ? geoSnapshot.data() : undefined
        const geoPoint = new GeoPoint(lat, lng)
        const now = FirebaseTimestamp.now()

        const afterStoreGeoSearch: StoreGeoSearch = {
          d: {
            store_ref: storeRef,
            coordinates: geoPoint,
            icon_type: 'shop',
            store_name: store.name,
            store_address: address,
            image_url: store.image_urls && store.image_urls.length > 0 ? store.image_urls[0] : '',
            status: store.status as 'public' | 'private' | 'deleted',
            created_at: geoSnapshot.exists ? beforeStoreGeoSearch!.d.created_at : now,
            updated_at: now,
          },
          g: g,
          l: geoPoint,
        }
        dispatch(hideLoading())
        setStoreGeoSearch(afterStoreGeoSearch)
        setCenter({ lat, lng })
        setPosition({ lat, lng })
      } catch (e) {
        if (axios.isAxiosError(e)) {
          console.error(e.response)
        } else {
          console.error(e)
        }
        dispatch(hideLoading())
      }
    },
    [setStoreGeoSearch, setCenter, setPosition, storeId]
  )

  useEffect(() => {
    ;(async () => {
      if (!storeId) return
      const storeRef = db.collection('stores').doc(storeId)
      const ret = await fetchStoreGeoSearch(storeRef)
      if (!ret) return
      setStoreGeoSearch(ret)
      setCenter({ lat: ret.l.latitude, lng: ret.l.longitude })
      setPosition({ lat: ret.l.latitude, lng: ret.l.longitude })
    })()
  }, [])

  return {
    center,
    generateStoreGeoSearch,
    handlePosition,
    position,
    storeGeoSearch,
  }
}

export default useStoreGeoSearch
